import {
  Box,
  Table as MuiTable,
  TableHead as MuiTableHead,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  TableBody as MuiTableBody,
  Typography,
  SvgIcon,
  Collapse,
  TablePagination,
  TableFooter,
} from '@mui/material';
import { FunctionComponent, useState, Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from '@Components/Checkbox';
import FullScreenLoader from '@Components/FullScreenLoader';
import { TableData, TableHead } from '@Components/Table';
import {
  LoaderRow,
  PaginationStyles,
  StyledHead,
  StyledTableContainer,
} from '@Components/Table/Table.styles';
import TablePaginationActions from '@Components/TablePaginationActions';
import Icons from '@Constants/Icons.constants';
import { CommonConstants } from '@Constants/index';

import {
  CollapseButtonCell,
  CollapseIcon,
  CollapsePrimaryRow,
  CollapseTable,
} from './CollapsibleTable.styles';
import {
  CollapsibleTableData,
  CollapsibleTableProps,
  CollapsibleTableRow,
} from './CollapsibleTable.types';

const CollapsibleTable: FunctionComponent<CollapsibleTableProps> = (
  props: CollapsibleTableProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();

  /** PROPS */
  const {
    loading,
    checked,
    setChecked,
    verticalAlign,
    tableHead,
    rows,
    paginationProps,
  } = props;
  const { page, rowsPerPage, ...resPaginationProps } = paginationProps;

  /** STATES */
  const [open, setOpen] = useState<Record<string, boolean>>({});

  /**
   * Returns Table Head Data Alignment according to passed align value.
   */
  const getTableCellAlignment = (
    align?: 'left' | 'right' | 'center' | 'inherit'
  ): string => {
    switch (align) {
      case 'right':
        return '0 0 0 auto';
      case 'center':
        return '0 auto';
      default:
        return '0';
    }
  };

  /** Check if `open` state is null for all rows */
  const checkOpenState = () => {
    let flag = false;

    Object.keys(open).forEach((key: string) => {
      if (open[key]) {
        flag = true;
      }
    });

    return flag;
  };

  /** Returns id for Collapse Open State Array.  */
  const getOpenStateId = (id: string, data: TableData) => {
    const idOpenControlIndex = Object.keys(data).indexOf('idOpenControl');

    if (idOpenControlIndex !== -1) {
      return data[Object.keys(data)[idOpenControlIndex]] as string;
    }
    return `${id}-${data[Object.keys(data)[0]] as string}`;
  };

  /** Opens all the Collapsible Rows.  */
  const allOpenCollapse = () => {
    const newOpen: Record<string, boolean> = {};

    rows.forEach((row) => {
      const { id, data } = row;
      newOpen[id] = true;

      const getDataOpenState = (
        collapsibleChildData: CollapsibleTableData[],
        parentId: string
      ) =>
        collapsibleChildData.forEach((childRow) => {
          const { primaryData, collapsibleData } = childRow;
          if (collapsibleData) {
            newOpen[getOpenStateId(parentId, primaryData)] = true;
            getDataOpenState(collapsibleData, parentId);
          }
        });

      getDataOpenState(data, id);
      setOpen(newOpen);
    });
  };

  /** Opens/Closes all the Collapsible Rows. */
  const toggleCollapsibleRows = () => {
    if (!loading) {
      if (checkOpenState()) {
        setOpen({});
      } else {
        allOpenCollapse();
      }
    }
  };

  /** Handles onClick row collapsed state. */
  const handleCollapseRowClick = (id: string) => {
    if (open[id]) {
      Object.keys(open).forEach((key) => {
        if (key.includes(id)) {
          setOpen((prevState) => ({
            ...prevState,
            [key]: false,
          }));
        }
      });
    } else {
      setOpen((prevState) => ({
        ...prevState,
        [id]: true,
      }));
    }
  };

  /**
   * Handles Click on Checkbox - check/uncheck the table checkbox.
   */
  const handleTableCheckboxClick = (id: string): void => {
    if (!setChecked) return;

    const checkedList = checked ?? []; // Default to an empty array if checked is undefined
    const checkedIndex = checkedList.indexOf(id);
    let newChecked: readonly string[] = [];

    if (checkedIndex === -1) {
      newChecked = newChecked.concat(checkedList, id);
    } else if (checkedIndex === 0) {
      newChecked = newChecked.concat(checkedList.slice(1));
    } else if (checkedIndex === checkedList.length - 1) {
      newChecked = newChecked.concat(checkedList.slice(0, -1));
    } else if (checkedIndex > 0) {
      newChecked = newChecked.concat(
        checkedList.slice(0, checkedIndex),
        checkedList.slice(checkedIndex + 1)
      );
    }

    setChecked(newChecked);
  };

  /**
   * Checks if passed checkbox id is in checked state by checking its id
   * in the `checked` state array.
   */
  const isChecked = (id: string): boolean => (checked ?? []).indexOf(id) !== -1;

  /** Recursive Function
   *
   * Renders the Multi-level Collapsible Rows.
   */
  const renderCollapsibleRows = (
    data: CollapsibleTableData[],
    id: string,
    parent: string,
    align?: Record<string, TableHead['align']>,
    isCollapsibleData?: boolean
  ): ReactNode =>
    data.map(
      (
        collapsibleChildData: CollapsibleTableData,
        index: number
      ): JSX.Element => {
        const { primaryData, collapsibleData } = collapsibleChildData;
        const openStateKey = getOpenStateId(id, primaryData);

        const keys = Object.keys(primaryData);
        const idOpenControlIndex = keys.indexOf('idOpenControl');

        if (idOpenControlIndex !== -1) {
          keys.splice(idOpenControlIndex, 1);
        }

        return (
          <CollapseTable
            key={index}
            className={parent === 'root' ? 'CollapseTableParent' : ''}
          >
            <MuiTableHead>
              <MuiTableRow
                sx={{
                  backgroundColor: isCollapsibleData ? '#f8f8fa' : undefined,
                  verticalAlign: verticalAlign || 'middle',
                }}
              >
                <CollapseButtonCell
                  component={collapsibleData ? 'th' : 'td'}
                  onClick={() => handleCollapseRowClick(openStateKey)}
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === CommonConstants.ENTER_KEY) {
                      handleCollapseRowClick(openStateKey);
                    }
                  }}
                >
                  <Box width="2.4rem" height="2.4rem">
                    {collapsibleData && (
                      <CollapseIcon>
                        {open[openStateKey]
                          ? Icons.arrowDropUp
                          : Icons.arrowDropDown}
                      </CollapseIcon>
                    )}
                  </Box>
                </CollapseButtonCell>
                {keys.map((key: string, idx: number): JSX.Element => {
                  const alignment = align ? align[key] : 'inherit';
                  const margin = getTableCellAlignment(
                    alignment === 'inherit' ? tableHead[idx].align : alignment
                  );

                  return (
                    <MuiTableCell
                      key={idx}
                      component={collapsibleData ? 'th' : 'td'}
                      align={tableHead[idx].align}
                      sx={{
                        minWidth: `${tableHead[idx].width || 0}rem`,
                        // eslint-disable-next-line
                        maxWidth: `${tableHead[idx].maxWidth ? tableHead[idx].maxWidth + "rem" : "none"}`,
                        py: 1.4,
                      }}
                    >
                      <StyledHead sx={{ m: margin }}>
                        {primaryData[key]}
                      </StyledHead>
                    </MuiTableCell>
                  );
                })}
              </MuiTableRow>
              {collapsibleData && (
                <MuiTableRow>
                  <MuiTableCell sx={{ p: 0 }} colSpan={tableHead.length + 1}>
                    <Collapse
                      in={open[openStateKey]}
                      timeout="auto"
                      unmountOnExit
                    >
                      {renderCollapsibleRows(
                        collapsibleData,
                        id,
                        openStateKey,
                        align,
                        true
                      )}
                    </Collapse>
                  </MuiTableCell>
                </MuiTableRow>
              )}
            </MuiTableHead>
          </CollapseTable>
        );
      }
    );

  /** Renders Table Head. */
  const renderTableHead = (data: TableHead[]): ReactNode => (
    <MuiTableHead>
      <MuiTableRow className="tableHead-row">
        <CollapseButtonCell
          sx={{ py: 1.53 }}
          onClick={toggleCollapsibleRows}
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === CommonConstants.ENTER_KEY) {
              toggleCollapsibleRows();
            }
          }}
        >
          <CollapseIcon>
            {checkOpenState() ? Icons.arrowDropUp : Icons.arrowDropDown}
          </CollapseIcon>
        </CollapseButtonCell>
        {data.map(
          (
            { align, width, label, icon, onClick }: TableHead,
            index: number
          ): JSX.Element => (
            <MuiTableCell
              key={index}
              align={align}
              sx={{ minWidth: `${width || 0}rem` }}
            >
              <StyledHead
                role="button"
                tabIndex={onClick && 0}
                sx={{
                  margin: getTableCellAlignment(align),
                  cursor: onClick ? 'pointer' : 'initial',
                }}
                onClick={onClick}
              >
                <Typography variant="tableHeader">{label}</Typography>
                {icon && <SvgIcon>{Icons[icon]}</SvgIcon>}
              </StyledHead>
            </MuiTableCell>
          )
        )}
      </MuiTableRow>
    </MuiTableHead>
  );

  /** Renders Table Rows (Table Body). */
  const renderTableRows = (rowsData: CollapsibleTableRow[]): ReactNode => (
    <MuiTableBody sx={{ position: 'relative' }}>
      {rowsData.map((row: CollapsibleTableRow, index: number) => {
        const { id, name, data, align } = row;
        const checkedState = isChecked(id);

        return (
          <Fragment key={index}>
            <CollapsePrimaryRow
              tabIndex={0}
              onClick={() => handleCollapseRowClick(id)}
              onKeyPress={(e) => {
                if (e.key === CommonConstants.ENTER_KEY) {
                  handleCollapseRowClick(id);
                }
              }}
            >
              <CollapseButtonCell sx={{ py: 1.4 }}>
                <CollapseIcon>
                  {open[id] ? Icons.arrowDropUp : Icons.arrowDropDown}
                </CollapseIcon>
              </CollapseButtonCell>
              <MuiTableCell sx={{ py: 1.4 }} colSpan={tableHead.length + 1}>
                <Box display="flex" alignItems="center">
                  {checked && setChecked && (
                    <Checkbox
                      checked={checkedState}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        handleTableCheckboxClick(id);
                      }}
                      sx={{ mr: 2.4 }}
                    />
                  )}
                  {typeof name === 'string' ? (
                    <Typography variant="main" fontWeight="bold">
                      {name}
                    </Typography>
                  ) : (
                    name
                  )}
                </Box>
              </MuiTableCell>
            </CollapsePrimaryRow>
            <MuiTableRow>
              <MuiTableCell sx={{ p: 0 }} colSpan={tableHead.length + 1}>
                <Collapse in={open[id]} timeout="auto" unmountOnExit>
                  {renderCollapsibleRows(data, id, 'root', align)}
                </Collapse>
              </MuiTableCell>
            </MuiTableRow>
          </Fragment>
        );
      })}
      {/* LOADER */}
      {loading && (
        <LoaderRow>
          <MuiTableCell sx={{ width: '100%', height: '100%', p: '0' }}>
            <FullScreenLoader LoaderProps={{ size: '3.5rem' }} />
          </MuiTableCell>
        </LoaderRow>
      )}
      {/* Text to show when no data is present */}
      {!rows.length && (
        <MuiTableRow className="tableBody-row">
          <MuiTableCell colSpan={tableHead.length + 1}>
            <Typography
              variant="main"
              fontWeight="bold"
              textAlign="center"
              sx={{ my: 3.2, color: 'action.disabled' }}
            >
              {t('common.table.No data to show')}
            </Typography>
          </MuiTableCell>
        </MuiTableRow>
      )}
    </MuiTableBody>
  );

  /** Renders Table Pagination (Table Footer). */
  const renderTablePagination = () => (
    <TablePagination
      labelRowsPerPage={t('common.table.Rows per page')}
      page={page}
      rowsPerPage={rowsPerPage}
      {...resPaginationProps}
      component="div"
      sx={PaginationStyles}
      ActionsComponent={TablePaginationActions}
      SelectProps={{
        variant: 'outlined',
        inputProps: {
          'aria-label': 'rows per page',
        },
        MenuProps: {
          transformOrigin: {
            vertical: -2,
            horizontal: 37.5,
          },
        },
      }}
    />
  );

  return (
    <>
      <StyledTableContainer>
        <MuiTable>
          {renderTableHead(tableHead)}
          {renderTableRows(rows)}
          {/* TABLE FOOTER */}
          <TableFooter>
            <MuiTableRow sx={{ backgroundColor: 'background.paper' }}>
              <MuiTableCell sx={{ p: 0.05 }} colSpan={tableHead.length + 1} />
            </MuiTableRow>
          </TableFooter>
          {/* TABLE FOOTER */}
        </MuiTable>
      </StyledTableContainer>
      {!loading && !!rows.length && renderTablePagination()}
    </>
  );
};

export default CollapsibleTable;
